// Added by Sejune Oh on 2020-03-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'mobx-react'
import { jssPreset } from '@material-ui/styles'
import jss from 'jss'

// Logger
import Logger from '@/helper/Logger'
import { rootStore } from '@/stores'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface RegisterWebComponentArgs {
  defaultKey?: string
  customElementName: string
  Component: any
  styles?: Record<string, Record<string, any>>
  wrapperStyles?: string
}

interface WebComponentWrapperProps {
  _container: HTMLElement | null
  _actuatorId: string | null
  _recvDataFromCore: RecvDataFromCore | null
  _evtFlag: boolean
  _classes?: Record<string, Record<string, any>>
}

interface RecvDataFromCore {
  dataid: string
  data: any
}

export interface WebComponentProps {
  customElementName: string
  sendDataToCore(dataid: string, data: any): void
  recvDataFromCore: RecvDataFromCore | null
  classes?: Record<string, any>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * [registerWebComponent]
 *
 * @param Component         : React Component
 * @param customElementName : Custom element's tag name
 */
const registerWebComponent = ({
  defaultKey,
  Component,
  customElementName,
  styles,
  wrapperStyles,
}: RegisterWebComponentArgs) => {
  class WebComponentWrapper extends HTMLElement implements WebComponentWrapperProps {
    _container: HTMLElement | null
    _actuatorId: string | null
    _recvDataFromCore: RecvDataFromCore | null
    _evtFlag: boolean = false
    _classes: Record<string, Record<string, any>>

    constructor() {
      super()

      this._container = null
      this._actuatorId = null
      this._recvDataFromCore = null
      this._classes = {}
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // ! Watch component's props
    static get observedAttributes() {
      return ['key', 'dispatched']
    }

    get resizeEvtName() {
      return `${this._actuatorId}.resize`
    }

    get recvEvtName() {
      return `${this._actuatorId}.recvDataFromCore`
    }

    get sendEvtName() {
      return `${this._actuatorId}.sendDataToCore`
    }

    get insertionPoint() {
      return `${customElementName}-insertion-point`
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // ! Initialize
    private _initProps() {
      this._actuatorId = this.getAttribute('key') || defaultKey || 'unknown'
      if (!this._actuatorId) {
        Logger.errLog('_initProps:: Should pass the actuator id for react component')
      }
    }

    // ! Initialize style
    private _initJSSSetup ($el: HTMLStyleElement) {
      jss.setup({
        ...jssPreset(),
        insertionPoint: $el,
        createGenerateId: () => {
          let counter = 0
          const uniqueKey = this._actuatorId ? this._actuatorId.replace('.', '') : 'undefined'
          return (rule: any, sheet: any) => `${rule.key}-${uniqueKey}-${counter++}`
        }
      })
    }

    private _initClasses() {
      const sheets: Record<string, any> = {}

      for (let componentName in styles) {
        const sheet = jss.createStyleSheet({
          ...styles[componentName],
        },
        {
          meta: componentName.toLocaleLowerCase()
        })
        sheet.attach()
        sheets[componentName] = sheet.classes
      }

      this._classes = sheets
    }

    // ! Render
    private _render() {
      if (!this._container) return

      ReactDOM.render(
        <Provider {...rootStore}>
          <Component
            customElementName={customElementName}
            sendDataToCore={(dataid: string, data: any) => this._sendDataToCore(dataid, data)}
            recvDataFromCore={this._recvDataFromCore}
            classes={this._classes}
          />
        </Provider>,
        this._container
      )
    }

    private _addEvtListener() {
      if (this._evtFlag) return

      this.addEventListener(this.resizeEvtName, this._resizeEvtHandler, true)
      this.addEventListener(this.recvEvtName, this._recvDataFromCoreEvtHandler, true)

      this._evtFlag = true
    }

    private _clearEvtListener() {
      if (!this._evtFlag) return

      this.removeEventListener(this.resizeEvtName, this._resizeEvtHandler, true)
      this.removeEventListener(this.recvEvtName, this._recvDataFromCoreEvtHandler, true)

      this._evtFlag = false
    }

    private _resizeEvtHandler({ detail }: any) {}

    private _recvDataFromCoreEvtHandler({ detail: { dataid, data } }: any) {
      this._recvDataFromCore = {
        dataid,
        data,
      }
    }

    private _sendDataToCore(dataid: string, data: any) {
      const event = new CustomEvent(this.sendEvtName, { detail: { dataid, data } })
      this.dispatchEvent(event)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // ! HTML Element's attr changed callback (Rerender react component)
    attributeChangedCallback(name: string, oldValue: any, newValue: any) {
      if (name === 'key') {
        this._actuatorId = newValue
        this._clearEvtListener()
        this._addEvtListener()
      }

      this._render()
    }

    // ! Connected callback
    connectedCallback() {
      // https://github.com/facebook/react/issues/9242
      const shadow = this.attachShadow({ mode: 'open' })
      const $style = shadow.appendChild(document.createElement('style'))
      const $container = shadow.appendChild(document.createElement('template'))
      $container.style.cssText = wrapperStyles || 'width: 100%; height: 100%; display: flex; overflow: hidden;'

      this._initJSSSetup($style)

      // ! Single stylesheet
      if (styles) this._initClasses()

      shadow.removeChild($style)

      Object.defineProperty($container, 'ownerDocument', { value: shadow })
      // @ts-ignore
      shadow.createElement = (...args) => document.createElement(...args)
      // @ts-ignore
      shadow.createElementNS = (...args) => document.createElementNS(...args)
      // @ts-ignore
      shadow.createTextNode = (...args) => document.createTextNode(...args)

      this._container = $container
      this._initProps()
      this._addEvtListener()
      this._render()
    }

    // ! Disconnected callback
    disconnectedCallback() {
      this._clearEvtListener()

      if (!this._container) return
      ReactDOM.unmountComponentAtNode(this._container)
    }
  }

  customElements.define(customElementName, WebComponentWrapper)
}

export default registerWebComponent
